import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowTrendingUpIcon, WalletIcon, CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import portfolioImage1 from '../assets/portfolio/2150769939.jpg';
import portfolioImage2 from '../assets/portfolio/2149250219.jpg';
import portfolioImage3 from '../assets/portfolio/55634.jpg';

const PortfolioSection = () => {
  const [selectedModal, setSelectedModal] = useState(null);

  const features = [
    {
      icon: ArrowTrendingUpIcon,
      title: "Grow your wealth",
      description: "Build diversified portfolios with traditional and innovative investment strategies.",
      image: portfolioImage1,
      imageAlt: "Professional investment portfolio management",
      modalContent: {
        title: "Grow your wealth",
        subtitle: "Let your assets work through diversified investment strategies and professional portfolio management.",
        sections: [
          {
            title: "Our annual savings rates for supported digital assets.",
            rates: [
              { icon: "₿", name: "BTC", rate: "Up to 7%", color: "bg-orange-500" },
              { icon: "Ξ", name: "ETH", rate: "Up to 8%", color: "bg-blue-500" },
              { icon: "N", name: "NEXO", rate: "Up to 12%", color: "bg-gray-900" },
              { icon: "₮", name: "USDT", rate: "Up to 16%", color: "bg-green-500" },
              { icon: "◈", name: "SOL", rate: "Up to 8%", color: "bg-black" },
              { icon: "£", name: "GBPx", rate: "Up to 15%", color: "bg-blue-600" }
            ]
          },
          {
            title: "Earn daily compound interest with Flexible Savings",
            description: "Build your wealth with daily compound interest while keeping your funds available anytime."
          },
          {
            title: "Earn higher rates for longer with Fixed-term Savings", 
            description: "Earn more for a specific time period and achieve your long-term savings goals."
          }
        ]
      }
    },
    {
      icon: WalletIcon,
      title: "Manage your portfolio",
      description: "Access comprehensive portfolio management with traditional and digital asset solutions.",
      image: portfolioImage2,
      imageAlt: "Digital asset management platform",
      modalContent: {
        title: "Manage your portfolio",
        subtitle: "Access comprehensive investment solutions through our integrated wealth management platform.",
        sections: [
          {
            title: "Diversify your portfolio with the Segway Exchange",
            features: [
              {
                icon: "🔄",
                title: "Exchange 100+ digital assets like BTC, ETH, or SOL and get cashback on each trade."
              },
              {
                icon: "🎯", 
                title: "Set Recurring Buys orders to execute trades at your desired price automatically."
              }
            ]
          },
          {
            title: "Unlock liquidity with a Credit Line from 2.9%",
            description: "Use your crypto as collateral to finance your expenses without selling your digital assets."
          },
          {
            title: "Capitalize on the uptrends and downtrends with Futures",
            description: "Trade perpetual Futures with up to 100x leverage to take advantage of price moves in either direction."
          }
        ]
      }
    },
    {
      icon: CreditCardIcon,
      title: "Access innovative solutions",
      description: "Explore Web3 technology and digital asset opportunities within your portfolio.",
      image: portfolioImage3,
      imageAlt: "Cashback card payment system",
      modalContent: {
        title: "Access innovative solutions",
        subtitle: "Integrate cutting-edge Web3 technology and digital assets into your investment strategy.",
        sections: [
          {
            title: "Credit Mode",
            subtitle: "Spend without selling your crypto.",
            features: [
              "Use your crypto as collateral to finance your expenses.",
              "Get up to 2% in cashback on purchases.", 
              "Your expenses are tied to your Credit Line starting from 2.9% annual interest."
            ],
            image: portfolioImage1
          },
          {
            title: "Debit Mode", 
            subtitle: "Spend and grow your savings.",
            features: [
              "Earn up to 14% annual interest on your balance and have it ready to spend anytime.",
              "Take control and prioritize your spending assets with a simple drag and drop."
            ],
            image: portfolioImage2
          }
        ]
      }
    }
  ];

  const openModal = (index) => {
    setSelectedModal(index);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedModal(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <section className="py-16 lg:py-24 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 1.2, 
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2 
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-left mb-12 lg:mb-16 max-w-4xl"
          >
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6 leading-tight font-heading"
            >
              Build your portfolio from a single app, designed for the forward-thinking investor.
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 1, 
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: index * 0.2 
                }}
                viewport={{ once: true, margin: "-50px" }}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                className="group cursor-pointer"
                onClick={() => openModal(index)}
              >
                <motion.div 
                  className="bg-gray-900 rounded-2xl p-6 lg:p-8 text-white overflow-hidden relative min-h-[450px] lg:min-h-[500px]"
                  whileHover={{ 
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    transition: { duration: 0.3 }
                  }}
                  style={{
                    backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.3) 100%), url(${feature.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Content overlay */}
                  <div className="relative z-10 h-full flex flex-col">
                    {/* Title at top */}
                    <motion.h3 
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.2 + 0.4 }}
                      viewport={{ once: true }}
                      className="text-xl lg:text-2xl font-bold mb-4 text-white drop-shadow-lg"
                    >
                      {feature.title}
                    </motion.h3>

                    {/* Icon */}
                    <motion.div 
                      className="w-12 h-12 lg:w-14 lg:h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center absolute top-4 right-4 group-hover:bg-white/20 transition-all duration-300"
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <feature.icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </motion.div>
                  </div>

                  {/* Background overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                </motion.div>

                {/* Description below card */}
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.8 }}
                  viewport={{ once: true }}
                  className="text-gray-600 text-sm lg:text-base leading-relaxed mt-4"
                >
                  {feature.description}
                </motion.p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedModal !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black rounded-xl flex items-center justify-center text-white hover:bg-gray-800 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[90vh]">
                {selectedModal !== null && (
                  <div className="p-6">
                    {/* Header Image */}
                    {features[selectedModal].modalContent.sections[0].image && (
                      <div className="mb-6 rounded-2xl overflow-hidden">
                        <img 
                          src={features[selectedModal].image} 
                          alt={features[selectedModal].imageAlt}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {features[selectedModal].modalContent.title}
                    </h2>
                    
                    {/* Subtitle */}
                    <p className="text-gray-600 text-base lg:text-lg mb-6">
                      {features[selectedModal].modalContent.subtitle}
                    </p>

                    {/* Sections */}
                    {features[selectedModal].modalContent.sections.map((section, sectionIndex) => (
                      <div key={sectionIndex} className="mb-8">
                        <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-4">
                          {section.title}
                        </h3>
                        
                        {section.subtitle && (
                          <p className="text-teal-600 font-medium text-sm lg:text-base mb-4">
                            {section.subtitle}
                          </p>
                        )}

                        {section.description && (
                          <p className="text-gray-600 text-sm lg:text-base mb-4">
                            {section.description}
                          </p>
                        )}

                        {/* Rates Grid */}
                        {section.rates && (
                          <div className="space-y-4 mb-6">
                            {section.rates.map((rate, rateIndex) => (
                              <div key={rateIndex} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-10 h-10 ${rate.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                                    {rate.icon}
                                  </div>
                                  <div>
                                    <p className="font-bold text-gray-900">{rate.rate}</p>
                                    <p className="text-gray-500 text-sm">On {rate.name}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Features List */}
                        {section.features && (
                          <div className="space-y-4 mb-6">
                            {section.features.map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-start space-x-3">
                                {typeof feature === 'object' ? (
                                  <>
                                    <div className="text-2xl mt-1">{feature.icon}</div>
                                    <p className="text-gray-600 text-sm lg:text-base">{feature.title}</p>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                                      <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                    <p className="text-gray-600 text-sm lg:text-base">{feature}</p>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Section Image */}
                        {section.image && sectionIndex > 0 && (
                          <div className="rounded-2xl overflow-hidden mb-4">
                            <img 
                              src={section.image} 
                              alt={section.title}
                              className="w-full h-32 object-cover"
                            />
                          </div>
                        )}

                        {/* Learn More Button */}
                        {sectionIndex === 0 && (
                          <Link to="/apply-now">
                            <button className="w-full mt-4 px-6 py-3 border border-gray-300 rounded-2xl text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                              <span>Learn more</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          </Link>
                        )}
                      </div>
                    ))}

                    {/* Sign Up Button */}
                    <Link to="/apply-now">
                      <button className="w-full bg-black text-white px-6 py-4 rounded-2xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2">
                        <span>Sign up</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioSection; 